import { useCallback, useMemo, useState } from 'react';
import { useFloating } from '@floating-ui/react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import PopperMenu, { PopperMenuItem } from '@trinity/chat-components/components/Popper/PopperMenu.js';
import { Portal } from '@trinity/chat-components/components/Portal/Portal.js';
import { useZIndexMiddleware } from '@trinity/chat-components/components/presentation/popper-modifiers.js';
import { MoreVerticalAnchorSmall } from '@/components/presentation/Anchor';
import { useToggleTagSumaryVersionsContext, useRefreshSummariesContext, useSummaryFeedbackClickedContext } from '@/containers/ConferenceTag.Summary/context';
import type { ConferenceTagSummaryItem } from '@/types';

type Props = {
  className?: string;
  item: ConferenceTagSummaryItem;
};

export const TagSummaryContextMenu = (props: Props) => {

  const [open, setOpen] = useState(false);
  const toggleVersionsModal = useToggleTagSumaryVersionsContext(props.item);
  const { refresh, ...refreshCtx } = useRefreshSummariesContext();
  const openFeedbackModal = useSummaryFeedbackClickedContext(props.item);

  const zIndexMiddleware = useZIndexMiddleware();

  const { refs, floatingStyles } = useFloating({
    middleware: [
      zIndexMiddleware,
    ],
    placement: 'bottom-start',
  });

  const handleBlur = useCallback(() => {
    setOpen(false);
  }, []);

  const handleClick = useCallback(() => {
    setOpen(true);
  }, []);

  const handleRefresh = useCallback(() => {
    if (refreshCtx.isRefreshing) return;
    return refresh(props.item.tag.id);
  }, [refresh, refreshCtx.isRefreshing, props.item.tag.id]);

  const refreshItem = useMemo(() => {
    return {
      children: 'Refresh',
      disabled: refreshCtx.isRefreshing,
      onClick: handleRefresh,
    };
  }, [handleRefresh, refreshCtx.isRefreshing]);

  const versionsItem = useMemo(() => {
    const totalVersions = props.item.summary?.meta?.totalVersions;
    const hasOtherVersions = totalVersions > 1;
    const children = [
      'View other versions',
      hasOtherVersions && `(${totalVersions})`,
    ].filter(Boolean).join(' ');

    return {
      children,
      disabled: !hasOtherVersions,
      onClick: toggleVersionsModal,
    };
  }, [props.item.summary?.meta?.totalVersions, toggleVersionsModal]);
  /*
  const feedbackItem = useMemo(() => {
    const hasFeedback = props.item.summary?.meta?.hasFeedback;
    return {
      children: 'View feedback',
      disabled: false,
      onClick: openFeedbackModal,
    };
  }, [props.item.summary?.meta?.hasFeedback, openFeedbackModal]);
  */
  const items = useMemo(() => {
    return [/* feedbackItem,  */refreshItem, versionsItem];
  }, [/* feedbackItem,  */refreshItem, versionsItem]);

  return (
    <>
      <div
        className={props.className}
        ref={refs.setReference}
        onClick={handleClick}>
        <MoreVerticalAnchorSmall
          open={open} />
      </div>
      {open &&
        <Portal>
          <ClickAwayListener
            mouseEvent='onMouseDown'
            onClickAway={handleBlur}
            touchEvent='onTouchStart'>
            <div
              ref={refs.setFloating}
              style={floatingStyles}>
              <PopperMenu>
                {items.map((m, i) => {
                  return (
                    <PopperMenuItem
                      key={i}
                      disabled={m.disabled}
                      onClick={() => {
                        m.onClick();
                        setOpen(false);
                      }}>
                      {m.children}
                    </PopperMenuItem>
                  );
                })}
              </PopperMenu>
            </div>
          </ClickAwayListener>
        </Portal>
      }
    </>
  );
};